<template>
    <div class="workforce-settings-page">
        <header class="workforce-settings-header">
            <div class="workforce-settings-header__title">
                <b-button
                    variant="link"
                    class="workforce-settings-header__back"
                    title="Вернуться к распределению сотрудников"
                    @click="goToBoard"
                >
                    <i class="fas fa-arrow-left" aria-hidden="true"></i>
                    <span>К распределению</span>
                </b-button>
                <div>
                    <h1>Настройки модуля «Сотрудники»</h1>
                    <p>Доступ, доска распределения и правила назначений</p>
                </div>
            </div>

            <div v-if="!accessDenied" class="workforce-settings-header__actions">
                <b-button
                    variant="outline-secondary"
                    :disabled="loading || saving"
                    @click="loadSettings"
                >
                    <i
                        class="fas fa-sync-alt mr-1"
                        :class="{'fa-spin': loading}"
                        aria-hidden="true"
                    ></i>
                    Обновить
                </b-button>
                <b-button
                    variant="primary"
                    :disabled="loading || saving"
                    @click="saveSettings"
                >
                    <b-spinner v-if="saving" small class="mr-1"></b-spinner>
                    <i v-else class="fas fa-save mr-1" aria-hidden="true"></i>
                    Сохранить
                </b-button>
            </div>
        </header>

        <b-alert v-if="accessDenied" show variant="warning">
            <i class="fas fa-lock mr-1" aria-hidden="true"></i>
            У вас нет доступа к настройкам модуля «Сотрудники».
        </b-alert>

        <template v-else>
            <b-alert
                v-if="errorMessage"
                show
                dismissible
                variant="danger"
                @dismissed="errorMessage = ''"
            >
                {{ errorMessage }}
            </b-alert>

            <b-alert
                v-if="successMessage"
                show
                dismissible
                variant="success"
                @dismissed="successMessage = ''"
            >
                {{ successMessage }}
            </b-alert>

            <div v-if="loading && !hasLoaded" class="workforce-settings-state">
                <b-spinner small variant="primary"></b-spinner>
                <span>Загружаем настройки…</span>
            </div>

            <form v-else-if="hasLoaded" @submit.prevent="saveSettings">
                <b-tabs
                    pills
                    card
                    class="workforce-settings-main-tabs"
                    nav-class="workforce-settings-main-tabs__nav"
                >
                    <b-tab title="Доступ" active>
                        <section class="workforce-settings-section">
                            <div class="workforce-settings-section__heading">
                                <div class="workforce-settings-section__icon">
                                    <i class="fas fa-user-shield" aria-hidden="true"></i>
                                </div>
                                <div>
                                    <h2>Доступ к модулю</h2>
                                    <p>
                                        Права складываются по принципу «ИЛИ»: достаточно
                                        совпадения хотя бы с одним участником.
                                    </p>
                                </div>
                            </div>

                            <b-alert show variant="secondary" class="py-2">
                                Администратор имеет полный доступ. Роль Top сама по себе
                                дополнительных прав не даёт.
                            </b-alert>

                            <b-tabs pills card nav-class="workforce-settings-tabs">
                                <b-tab
                                    v-if="registryRight"
                                    :title="registryRight.title"
                                >
                                    <div class="workforce-access-intro">
                                        <h3>{{ registryRight.title }}</h3>
                                        <p>{{ registryRight.description }}</p>
                                    </div>
                                    <div class="workforce-access-scope workforce-access-scope--single">
                                        <MultipleParticipantsConfig
                                            :value="form.access_config.registry"
                                            :users="participantUsers"
                                            :departments="participantDepartments"
                                            :allowed-types="baseAccessTypes"
                                            participant-label="правило"
                                            type-label="Кому выдать право"
                                            all-text="Право получают все пользователи системы."
                                            :show-fallback-option="false"
                                            :show-priority="false"
                                            :empty-text="registryRight.emptyText"
                                            :hint-text="registryRight.hint"
                                            @input="value => onSimpleAccessInput('registry', value)"
                                        />
                                    </div>
                                </b-tab>

                                <b-tab
                                    v-for="right in scopedRights"
                                    :key="right.key"
                                    :title="right.title"
                                >
                                    <div class="workforce-access-intro">
                                        <h3>{{ right.title }}</h3>
                                        <p>{{ right.description }}</p>
                                    </div>

                                    <div class="workforce-access-scopes">
                                        <article class="workforce-access-scope">
                                            <h4>На всех объектах</h4>
                                            <p>
                                                Право действует для всех групп, объектов и
                                                общих зон доски.
                                            </p>
                                            <MultipleParticipantsConfig
                                                :value="form.access_config[right.key].all_objects"
                                                :users="participantUsers"
                                                :departments="participantDepartments"
                                                :allowed-types="baseAccessTypes"
                                                participant-label="правило"
                                                type-label="Кому выдать право"
                                                all-text="Право получают все пользователи системы."
                                                :show-fallback-option="false"
                                                :show-priority="false"
                                                empty-text="Право на все объекты остаётся только у администраторов."
                                                hint-text="Укажите пользователей или отделы, которым право нужно на всей доске."
                                                @input="value => onScopedAccessInput(right.key, 'all_objects', value)"
                                            />
                                        </article>

                                        <article class="workforce-access-scope">
                                            <h4>На своих объектах</h4>
                                            <p>
                                                Объектная изоляция проверяется отдельно для
                                                каждого объекта.
                                            </p>
                                            <MultipleParticipantsConfig
                                                :value="form.access_config[right.key].own_objects"
                                                :users="participantUsers"
                                                :departments="participantDepartments"
                                                :allowed-types="ownObjectAccessTypes"
                                                :show-object-roles="true"
                                                :object-role-options="objectRoleOptions"
                                                participant-label="правило"
                                                type-label="Кому выдать право"
                                                all-text="Право получают все пользователи системы."
                                                object-role-hint-text="Роль проверяется на конкретном объекте: прораб, куратор или пользователь с доступом."
                                                :show-fallback-option="false"
                                                :show-priority="false"
                                                empty-text="Правила для своих объектов не добавлены."
                                                hint-text="Конкретный пользователь или отдел получает право независимо от роли; для ограничения своим объектом выберите «Роль на объекте»."
                                                @input="value => onScopedAccessInput(right.key, 'own_objects', value)"
                                            />
                                        </article>
                                    </div>
                                </b-tab>

                                <b-tab title="Карточка сотрудника">
                                    <div class="workforce-card-access-heading">
                                        <div>
                                            <h3>Кто и что видит в карточке</h3>
                                            <p>
                                                Для каждой роли на объекте задайте собственный набор прав.
                                                Корпоративные правила определяют, кому и чьи карточки доступны,
                                                настраиваются ниже и складываются с объектными.
                                            </p>
                                        </div>
                                        <div class="workforce-card-access-heading__note">
                                            <i class="fas fa-shield-alt" aria-hidden="true"></i>
                                            <span>
                                                Права действуют и для собственной карточки сотрудника.
                                                Администратор имеет полный доступ ко всем карточкам.
                                                Подтверждать прибытие для доступа к карточке запланированного сотрудника не требуется.
                                            </span>
                                        </div>
                                    </div>

                                    <div class="workforce-card-access-create">
                                        <div>
                                            <strong>Права на своих объектах</strong>
                                            <span>
                                                Прораб, куратор и имеющий доступ настраиваются отдельно.
                                                Доступ включает фактически назначенных и запланированных
                                                на свои объекты сотрудников — даже до подтверждения прибытия.
                                            </span>
                                        </div>
                                        <div class="workforce-card-access-create__actions">
                                            <b-button
                                                variant="outline-primary"
                                                @click="addCardAccessRule"
                                            >
                                                <i class="fas fa-building mr-1" aria-hidden="true"></i>
                                                Добавить правило компании
                                            </b-button>
                                        </div>
                                    </div>

                                    <article
                                        v-for="(rule, ruleIndex) in form.access_config.card_access_rules"
                                        :key="rule.id"
                                        class="workforce-card-access-rule"
                                        :class="{'workforce-card-access-rule--open': isCardRuleExpanded(rule)}"
                                    >
                                        <header class="workforce-card-access-rule__header">
                                            <button
                                                type="button"
                                                class="workforce-card-access-rule__toggle"
                                                :aria-expanded="isCardRuleExpanded(rule) ? 'true' : 'false'"
                                                @click="toggleCardRule(rule)"
                                            >
                                                <span
                                                    class="workforce-card-access-rule__scope"
                                                    :class="`workforce-card-access-rule__scope--${rule.scope}`"
                                                >
                                                    {{ rule.scope === 'object'
                                                        ? 'Свои объекты'
                                                        : 'Правило компании' }}
                                                </span>
                                                <span class="workforce-card-access-rule__summary">
                                                    <strong>{{ cardRuleAudienceSummary(rule) }}</strong>
                                                    <small v-if="rule.scope === 'company'">Карточки: {{ cardRuleTargetSummary(rule) }}</small>
                                                    <small>{{ cardRulePermissionSummary(rule) }}</small>
                                                </span>
                                                <i
                                                    class="fas fa-chevron-down workforce-card-access-rule__chevron"
                                                    :class="{'workforce-card-access-rule__chevron--open': isCardRuleExpanded(rule)}"
                                                    aria-hidden="true"
                                                ></i>
                                            </button>
                                            <b-button
                                                v-if="rule.scope === 'company'"
                                                variant="link"
                                                size="sm"
                                                class="workforce-card-access-rule__delete"
                                                title="Удалить правило"
                                                aria-label="Удалить правило"
                                                @click="removeCardAccessRule(ruleIndex)"
                                            >
                                                <i class="fas fa-trash-alt" aria-hidden="true"></i>
                                            </b-button>
                                        </header>

                                        <div
                                            v-if="isCardRuleExpanded(rule)"
                                            class="workforce-card-access-rule__body"
                                        >
                                            <section class="workforce-card-access-audience">
                                                <div class="workforce-card-access-step">
                                                    <span>1</span>
                                                    <div>
                                                        <strong>Кому разрешено</strong>
                                                        <small v-if="rule.scope === 'object'">
                                                            Роль проверяется на объекте фактического или планового назначения сотрудника.
                                                        </small>
                                                        <small v-else>
                                                            Выберите пользователей, которым выдаётся право.
                                                        </small>
                                                    </div>
                                                </div>

                                                <div
                                                    v-if="rule.scope === 'object'"
                                                    class="workforce-card-access-role-fixed"
                                                >
                                                    <i class="fas fa-user-shield" aria-hidden="true"></i>
                                                    <div>
                                                        <strong>{{ cardRuleAudienceSummary(rule) }}</strong>
                                                        <small>
                                                            Права действуют только на сотрудников,
                                                            фактически назначенных или запланированных на объекты этой роли.
                                                            Учитываются текущие и будущие периоды плана, даже без подтверждённого прибытия.
                                                        </small>
                                                    </div>
                                                </div>

                                                <template v-else>
                                                    <div class="workforce-card-access-company-mode">
                                                        <button
                                                            type="button"
                                                            :class="{'is-active': cardRuleCompanyMode(rule) === 'all'}"
                                                            @click="setCardRuleCompanyMode(ruleIndex, 'all')"
                                                        >
                                                            Все пользователи
                                                        </button>
                                                        <button
                                                            type="button"
                                                            :class="{'is-active': cardRuleCompanyMode(rule) === 'selected'}"
                                                            @click="setCardRuleCompanyMode(ruleIndex, 'selected')"
                                                        >
                                                            Выбранные отделы и пользователи
                                                        </button>
                                                    </div>
                                                    <div
                                                        v-if="cardRuleCompanyMode(rule) === 'selected'"
                                                        class="workforce-card-access-company-fields"
                                                    >
                                                        <b-form-group label="Отделы учётных записей" class="mb-0">
                                                            <v-select
                                                                :value="cardRuleCompanyDepartmentIds(rule)"
                                                                :options="participantDepartments"
                                                                :reduce="option => option.value"
                                                                label="text"
                                                                multiple
                                                                :close-on-select="false"
                                                                placeholder="Выберите отделы"
                                                                @input="value => setCardRuleCompanyDepartments(ruleIndex, value)"
                                                            />
                                                        </b-form-group>
                                                        <b-form-group label="Отдельные пользователи" class="mb-0">
                                                            <v-select
                                                                :value="cardRuleCompanyUserIds(rule)"
                                                                :options="participantUsers"
                                                                :reduce="option => option.value"
                                                                label="text"
                                                                multiple
                                                                :close-on-select="false"
                                                                placeholder="При необходимости выберите сотрудников"
                                                                @input="value => setCardRuleCompanyUsers(ruleIndex, value)"
                                                            />
                                                        </b-form-group>
                                                    </div>
                                                </template>

                                                <div
                                                    v-if="rule.scope === 'company' && !cardRuleHasAudience(rule)"
                                                    class="workforce-card-access-warning"
                                                >
                                                    <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
                                                    Выберите хотя бы одного получателя права.
                                                </div>
                                            </section>

                                            <section v-if="rule.scope === 'company'" class="workforce-card-access-audience">
                                                <div class="workforce-card-access-step">
                                                    <span>2</span>
                                                    <div>
                                                        <strong>Чьи карточки доступны</strong>
                                                        <small>Отдел проверяется по учётной записи сотрудника. Человека без аккаунта можно выбрать по имени.</small>
                                                    </div>
                                                </div>
                                                <div class="workforce-card-access-company-mode">
                                                    <button type="button" :class="{'is-active': rule.targets.mode === 'all'}" @click="setCardRuleTargetMode(rule, 'all')">Все сотрудники</button>
                                                    <button type="button" :class="{'is-active': rule.targets.mode === 'selected'}" @click="setCardRuleTargetMode(rule, 'selected')">Выбранные отделы и сотрудники</button>
                                                </div>
                                                <div v-if="rule.targets.mode === 'selected'" class="workforce-card-access-company-fields">
                                                    <b-form-group label="Отделы учётных записей" class="mb-0">
                                                        <v-select v-model="rule.targets.department_ids" :options="participantDepartments" :reduce="option => option.value" label="text" multiple :close-on-select="false" placeholder="Выберите отделы" />
                                                    </b-form-group>
                                                    <b-form-group label="Отдельные сотрудники" class="mb-0">
                                                        <v-select v-model="rule.targets.employee_ids" :options="cardTargetEmployeeOptions" :reduce="option => option.value" label="text" multiple :close-on-select="false" placeholder="Выберите сотрудников" />
                                                    </b-form-group>
                                                </div>
                                                <div v-if="rule.targets.mode === 'selected' && !rule.targets.department_ids.length && !rule.targets.employee_ids.length" class="workforce-card-access-warning">
                                                    Пока никто не выбран, правило не открывает карточки.
                                                </div>
                                            </section>

                                            <section class="workforce-card-access-permissions">
                                                <div class="workforce-card-access-permissions__heading">
                                                    <div class="workforce-card-access-step">
                                                        <span>{{ rule.scope === 'company' ? 3 : 2 }}</span>
                                                        <div>
                                                            <strong>Что разрешено</strong>
                                                            <small>«Изменять» автоматически включает просмотр.</small>
                                                        </div>
                                                    </div>
                                                    <div class="workforce-card-access-presets">
                                                        <button type="button" @click="applyCardRulePreset(ruleIndex, 'view')">
                                                            Только просмотр всего
                                                        </button>
                                                        <button type="button" @click="applyCardRulePreset(ruleIndex, 'manage')">
                                                            Максимальный доступ
                                                        </button>
                                                        <button type="button" @click="applyCardRulePreset(ruleIndex, 'none')">
                                                            Очистить
                                                        </button>
                                                    </div>
                                                </div>

                                                <div class="workforce-card-access-sections">
                                                    <div
                                                        v-for="section in cardSections"
                                                        :key="`${rule.id}-${section.key}`"
                                                        class="workforce-card-access-section"
                                                    >
                                                        <div class="workforce-card-access-section__label">
                                                            <strong>{{ section.label }}</strong>
                                                            <small v-if="section.hint">{{ section.hint }}</small>
                                                        </div>
                                                        <div
                                                            class="workforce-card-access-levels"
                                                            :class="{'workforce-card-access-levels--view-only': !section.manage}"
                                                        >
                                                            <button
                                                                type="button"
                                                                :class="{'is-active is-none': cardRuleSectionLevel(rule, section.key) === 'none'}"
                                                                @click="setCardRuleSectionLevel(ruleIndex, section.key, 'none')"
                                                            >
                                                                Нет
                                                            </button>
                                                            <button
                                                                type="button"
                                                                :class="{'is-active is-view': cardRuleSectionLevel(rule, section.key) === 'view'}"
                                                                @click="setCardRuleSectionLevel(ruleIndex, section.key, 'view')"
                                                            >
                                                                <i class="fas fa-eye" aria-hidden="true"></i>
                                                                Смотреть
                                                            </button>
                                                            <button
                                                                v-if="section.manage"
                                                                type="button"
                                                                :class="{'is-active is-manage': cardRuleSectionLevel(rule, section.key) === 'manage'}"
                                                                @click="setCardRuleSectionLevel(ruleIndex, section.key, 'manage')"
                                                            >
                                                                <i class="fas fa-pen" aria-hidden="true"></i>
                                                                Изменять
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </section>
                                        </div>
                                    </article>
                                </b-tab>

                                <b-tab
                                    v-for="right in actionRights"
                                    :key="right.key"
                                    :title="right.title"
                                >
                                    <div class="workforce-access-intro">
                                        <h3>{{ right.title }}</h3>
                                        <p>{{ right.description }}</p>
                                    </div>
                                    <div class="workforce-access-scope workforce-access-scope--single">
                                        <MultipleParticipantsConfig
                                            :value="form.access_config[right.key]"
                                            :users="participantUsers"
                                            :departments="participantDepartments"
                                            :allowed-types="baseAccessTypes"
                                            participant-label="правило"
                                            type-label="Кому выдать право"
                                            all-text="Право получают все пользователи системы."
                                            :show-fallback-option="false"
                                            :show-priority="false"
                                            :empty-text="right.emptyText"
                                            :hint-text="right.hint"
                                            @input="value => onSimpleAccessInput(right.key, value)"
                                        />
                                    </div>
                                </b-tab>
                            </b-tabs>
                        </section>
                    </b-tab>

                    <b-tab title="Доска распределения">
                        <section class="workforce-settings-section">
                    <div class="workforce-settings-section__heading">
                        <div class="workforce-settings-section__icon">
                            <i class="fas fa-users" aria-hidden="true"></i>
                        </div>
                        <div>
                            <h2>Состав доски по умолчанию</h2>
                            <p>
                                Эти параметры определяют, какие сотрудники появляются на доске
                                сразу после открытия.
                            </p>
                        </div>
                    </div>

                    <b-form-group
                        label="Отделы, показываемые по умолчанию"
                        label-for="workforce-default-departments"
                        class="mb-4"
                    >
                        <v-select
                            id="workforce-default-departments"
                            v-model="form.default_department_ids"
                            :options="defaultDepartmentOptions"
                            :reduce="option => option.value"
                            label="text"
                            multiple
                            :close-on-select="false"
                            placeholder="Все отделы"
                        ></v-select>
                        <small class="text-muted d-block mt-2">
                            Если ничего не выбрано, доска не ограничивается отделами. Сотрудники
                            отдела «Линия» добавляются сервером независимо от этого списка.
                        </small>
                    </b-form-group>

                    <b-form-checkbox
                        v-model="form.show_assigned_outside_departments"
                        switch
                    >
                        Показывать назначенных сотрудников из других отделов
                    </b-form-checkbox>
                    <small class="text-muted d-block mt-1">
                        Сотрудник останется видимым на объекте или группе, даже если его отдел не
                        входит в список выше.
                    </small>

                    <b-alert show variant="light" class="border mt-3 mb-0 py-2">
                        Выбор отделов управляет только составом доски. Чтобы назначать и
                        перемещать сотрудников, отдельно выдайте право
                        <strong>«Перемещение сотрудников»</strong> на вкладке «Доступ».
                    </b-alert>
                        </section>
                    </b-tab>

                    <b-tab title="Состав списков">
                        <section class="workforce-settings-section">
                            <div class="workforce-settings-section__heading">
                                <div class="workforce-settings-section__icon">
                                    <i class="fas fa-user-minus" aria-hidden="true"></i>
                                </div>
                                <div>
                                    <h2>Кого не выводить в рабочих списках</h2>
                                    <p>
                                        Доска и годовой план настраиваются независимо. Исключение
                                        скрывает сотрудника из списка и сводных количеств, но не
                                        меняет его карточку, назначения, план или права доступа.
                                    </p>
                                </div>
                            </div>

                            <div class="workforce-list-composition-grid">
                                <div class="workforce-list-composition-card">
                                    <h3>
                                        <i class="fas fa-columns mr-1" aria-hidden="true"></i>
                                        Доска распределения
                                    </h3>
                                    <p>
                                        Эти сотрудники не появятся в текущих блоках доски и её
                                        счётчиках.
                                    </p>
                                    <b-form-group label="Исключить отделы">
                                        <v-select
                                            v-model="form.view_exclusions.board.department_ids"
                                            :options="exclusionDepartmentOptions"
                                            :reduce="option => option.value"
                                            label="text"
                                            multiple
                                            :close-on-select="false"
                                            placeholder="Никого не исключать по отделу"
                                        ></v-select>
                                    </b-form-group>
                                    <b-form-group label="Скрывать сотрудников">
                                        <v-select
                                            v-model="form.view_exclusions.board.employee_ids"
                                            @input="syncVisibilitySelection('board', 'employee_ids')"
                                            :options="exclusionEmployeeOptions"
                                            :reduce="option => option.value"
                                            label="text"
                                            multiple
                                            :close-on-select="false"
                                            placeholder="Никого не исключать отдельно"
                                        ></v-select>
                                    </b-form-group>
                                    <b-form-group label="Показывать независимо от отдела" class="mb-0">
                                        <v-select v-model="form.view_exclusions.board.included_employee_ids"
                                            :options="exclusionEmployeeOptions" :reduce="option => option.value" label="text"
                                            multiple :close-on-select="false" placeholder="По общим настройкам"
                                            @input="syncVisibilitySelection('board', 'included_employee_ids')" />
                                    </b-form-group>
                                </div>

                                <div class="workforce-list-composition-card">
                                    <h3>
                                        <i class="far fa-calendar-alt mr-1" aria-hidden="true"></i>
                                        Годовой план
                                    </h3>
                                    <p>
                                        Эти сотрудники не появятся в строках плана и недельной
                                        сводке.
                                    </p>
                                    <b-form-group label="Исключить отделы">
                                        <v-select
                                            v-model="form.view_exclusions.plan.department_ids"
                                            :options="exclusionDepartmentOptions"
                                            :reduce="option => option.value"
                                            label="text"
                                            multiple
                                            :close-on-select="false"
                                            placeholder="Никого не исключать по отделу"
                                        ></v-select>
                                    </b-form-group>
                                    <b-form-group label="Скрывать сотрудников">
                                        <v-select
                                            v-model="form.view_exclusions.plan.employee_ids"
                                            @input="syncVisibilitySelection('plan', 'employee_ids')"
                                            :options="exclusionEmployeeOptions"
                                            :reduce="option => option.value"
                                            label="text"
                                            multiple
                                            :close-on-select="false"
                                            placeholder="Никого не исключать отдельно"
                                        ></v-select>
                                    </b-form-group>
                                    <b-form-group label="Показывать независимо от отдела" class="mb-0">
                                        <v-select v-model="form.view_exclusions.plan.included_employee_ids"
                                            :options="exclusionEmployeeOptions" :reduce="option => option.value" label="text"
                                            multiple :close-on-select="false" placeholder="По общим настройкам"
                                            @input="syncVisibilitySelection('plan', 'included_employee_ids')" />
                                    </b-form-group>
                                </div>
                            </div>

                            <b-alert show variant="light" class="border mt-3 mb-0 py-2">
                                Индивидуальные настройки важнее исключения отдела. Списки «Скрывать»
                                и «Показывать» связаны с настройками карточки сотрудника.
                                Для остальных сотрудников действуют общие исключения отделов.
                            </b-alert>
                        </section>
                    </b-tab>

                    <b-tab title="Несколько назначений">
                        <section class="workforce-settings-section">
                    <div class="workforce-settings-section__heading">
                        <div class="workforce-settings-section__icon">
                            <i class="fas fa-project-diagram" aria-hidden="true"></i>
                        </div>
                        <div>
                            <h2>Несколько назначений</h2>
                            <p>
                                Кто может одновременно числиться на нескольких объектах.
                                Условия складываются по принципу «ИЛИ».
                            </p>
                        </div>
                    </div>

                    <div class="workforce-access-scope workforce-access-scope--single">
                        <MultipleParticipantsConfig
                            :value="form.access_config.multi_assignment_eligibility"
                            :users="participantUsers"
                            :departments="participantDepartments"
                            :allowed-types="multiAssignmentEligibilityTypes"
                            :show-object-roles="true"
                            :object-role-options="objectRoleOptions"
                            participant-label="условие"
                            type-label="Кто может иметь несколько назначений"
                            all-text="Несколько назначений разрешены всем сотрудникам."
                            object-role-hint-text="Роль проверяется на объекте: прораб, куратор или пользователь с доступом."
                            :show-fallback-option="false"
                            :show-priority="false"
                            empty-text="Никому не разрешено дополнительное назначение."
                            hint-text="Можно указать конкретных сотрудников, отделы и роли на объекте. Достаточно совпадения с одним условием."
                            @input="onMultiAssignmentInput"
                        />
                    </div>

                    <b-alert show variant="light" class="border mt-3 mb-0 py-2">
                        Первое обычное назначение сотрудника этим правилом не ограничивается.
                        Оно применяется только при добавлении второго и последующих объектов.
                    </b-alert>
                        </section>
                    </b-tab>
                </b-tabs>

                <div class="workforce-settings-footer">
                    <b-button
                        type="button"
                        variant="outline-secondary"
                        :disabled="saving"
                        @click="goToBoard"
                    >
                        Отмена
                    </b-button>
                    <b-button type="submit" variant="primary" :disabled="saving">
                        <b-spinner v-if="saving" small class="mr-1"></b-spinner>
                        <i v-else class="fas fa-save mr-1" aria-hidden="true"></i>
                        Сохранить настройки
                    </b-button>
                </div>
            </form>
        </template>
    </div>
</template>

<script>
import api from '@/utils/api';
import {normalizeCardTargets} from '@/utils/employeeCardTargets';
import MultipleParticipantsConfig from '@/views/Requests/RequestTypes/MultipleParticipantsConfig/MultipleParticipantsConfig.vue';

const BASE_ACCESS_TYPES = ['all', 'specific_users', 'department'];
const OWN_OBJECT_ACCESS_TYPES = BASE_ACCESS_TYPES.concat('object_role');
const MULTI_ASSIGNMENT_ELIGIBILITY_TYPES = [
    'specific_users',
    'department',
    'object_role'
];
const OBJECT_ROLE_OPTIONS = [
    {value: 'main_staff', text: 'Прораб'},
    {value: 'curator', text: 'Куратор'},
    {value: 'has_access', text: 'Имеющий доступ к объекту'}
];
const CARD_SECTIONS = [
    {
        key: 'ppe_sizes',
        label: 'Размеры СИЗ',
        hint: 'Размеры одежды, обуви и других средств индивидуальной защиты.',
        manage: true
    },
    {
        key: 'contacts',
        label: 'Контакты',
        hint: 'Телефоны, email и Telegram.',
        manage: true
    },
    {
        key: 'personal',
        label: 'Личные данные',
        hint: 'Дата рождения и адреса. ФИО видно в списках сотрудников.',
        manage: true
    },
    {
        key: 'absences',
        label: 'Временные отсутствия',
        hint: 'Отпуск, больничный и другие отсутствия.',
        manage: true
    },
    {key: 'skills', label: 'Навыки', hint: 'Навыки и пояснения к ним.', manage: true},
    {
        key: 'characteristics',
        label: 'Характеристики',
        hint: 'Характеристики, КТУ и пояснения.',
        manage: true
    },
    {
        key: 'employment',
        label: 'Трудовые периоды',
        hint: 'Работодатель, должность и даты работы.',
        manage: true
    },
    {
        key: 'distribution',
        label: 'Распределение',
        hint: 'Перемещение задаётся отдельным правом доски.',
        manage: false
    },
    {
        key: 'history',
        label: 'История',
        hint: 'В истории скрываются события закрытых разделов.',
        manage: false
    }
];
const CARD_VIEW_SECTION_KEYS = CARD_SECTIONS.map(section => section.key);
const CARD_MANAGE_SECTION_KEYS = CARD_SECTIONS
    .filter(section => section.manage)
    .map(section => section.key);

function cloneJson(value) {
    return JSON.parse(JSON.stringify(value));
}

function emptyParticipantConfig() {
    return {participants: []};
}

const DEFAULT_OBJECT_CARD_VIEW_SECTIONS = [
    'contacts',
    'absences',
    'skills',
    'characteristics',
    'distribution',
    'history'
];

function objectRoleCardAccessRule(role, viewSections, manageSections) {
    return {
        id: `object_role_${role}`,
        scope: 'object',
        participants: [{type: 'object_role', roles: [role]}],
        view_sections: Array.isArray(viewSections) ? viewSections.slice() : [],
        manage_sections: Array.isArray(manageSections) ? manageSections.slice() : []
    };
}

function defaultObjectCardAccessRules() {
    return OBJECT_ROLE_OPTIONS.map(option => objectRoleCardAccessRule(
        option.value,
        DEFAULT_OBJECT_CARD_VIEW_SECTIONS,
        []
    ));
}

function defaultSettings() {
    return {
        access_config: {
            view: {
                all_objects: emptyParticipantConfig(),
                own_objects: {
                    participants: [
                        {
                            type: 'object_role',
                            roles: ['main_staff', 'curator', 'has_access']
                        }
                    ]
                }
            },
            move: {
                all_objects: emptyParticipantConfig(),
                own_objects: emptyParticipantConfig()
            },
            multi_assignment_eligibility: emptyParticipantConfig(),
            registry: emptyParticipantConfig(),
            characteristics: emptyParticipantConfig(),
            manage_employees: emptyParticipantConfig(),
            import: emptyParticipantConfig(),
            settings: emptyParticipantConfig(),
            card_access_rules: defaultObjectCardAccessRules()
        },
        default_department_ids: [],
        show_assigned_outside_departments: true,
        view_exclusions: {
            board: {department_ids: [], employee_ids: [], included_employee_ids: []},
            plan: {department_ids: [], employee_ids: [], included_employee_ids: []}
        },
        planning_colors: {}
    };
}

function positiveInteger(value) {
    const number = Number(value);
    return Number.isInteger(number) && number > 0 ? number : null;
}

function uniquePositiveIntegers(values) {
    const seen = {};
    return (Array.isArray(values) ? values : [])
        .map(positiveInteger)
        .filter(value => {
            if (!value || seen[value]) {
                return false;
            }
            seen[value] = true;
            return true;
        });
}

function normalizeViewExclusion(value) {
    const source = value && typeof value === 'object' ? value : {};
    return {
        department_ids: uniquePositiveIntegers(source.department_ids),
        employee_ids: uniquePositiveIntegers(source.employee_ids),
        included_employee_ids: uniquePositiveIntegers(source.included_employee_ids)
    };
}

function normalizeViewExclusions(value) {
    const source = value && typeof value === 'object' ? value : {};
    return {
        board: normalizeViewExclusion(source.board),
        plan: normalizeViewExclusion(source.plan)
    };
}

function normalizeParticipantConfig(value, fallback) {
    const source = value && typeof value === 'object'
        ? value
        : (fallback && typeof fallback === 'object' ? fallback : emptyParticipantConfig());
    return {
        participants: Array.isArray(source.participants)
            ? cloneJson(source.participants)
            : []
    };
}

function normalizeScopedRight(value, fallback) {
    const source = value && typeof value === 'object' ? value : {};
    const defaults = fallback && typeof fallback === 'object' ? fallback : {};
    return {
        all_objects: normalizeParticipantConfig(
            source.all_objects,
            defaults.all_objects
        ),
        own_objects: normalizeParticipantConfig(
            source.own_objects,
            defaults.own_objects
        )
    };
}

function normalizeCardSectionList(value, allowed) {
    return (Array.isArray(value) ? value : [])
        .filter((section, index, rows) => (
            allowed.indexOf(section) !== -1 && rows.indexOf(section) === index
        ));
}

function normalizeCardAccessRule(value, index) {
    const source = value && typeof value === 'object' ? value : {};
    const scope = source.scope === 'company' ? 'company' : 'object';
    const manageSections = normalizeCardSectionList(
        source.manage_sections,
        CARD_MANAGE_SECTION_KEYS
    );
    const viewSections = normalizeCardSectionList(
        source.view_sections,
        CARD_VIEW_SECTION_KEYS
    );
    manageSections.forEach(section => {
        if (viewSections.indexOf(section) === -1) viewSections.push(section);
    });
    return {
        id: String(source.id || `card_rule_${index + 1}`),
        scope,
        participants: normalizeParticipantConfig(source).participants,
        ...(scope === 'company' ? {targets: normalizeCardTargets(source.targets)} : {}),
        view_sections: viewSections,
        manage_sections: manageSections
    };
}

function canonicalCardRuleParticipants(rule) {
    const participants = Array.isArray(rule.participants) ? rule.participants : [];
    if (rule.scope === 'object') {
        const roles = [];
        participants.forEach(participant => {
            if (!participant || participant.type !== 'object_role') return;
            (Array.isArray(participant.roles) ? participant.roles : []).forEach(role => {
                if (
                    OBJECT_ROLE_OPTIONS.some(option => option.value === role)
                    && roles.indexOf(role) === -1
                ) roles.push(role);
            });
        });
        roles.sort();
        return roles.length ? [{type: 'object_role', roles}] : [];
    }
    if (participants.some(participant => participant && participant.type === 'all')) {
        return [{type: 'all'}];
    }
    const departmentIds = [];
    const userIds = [];
    participants.forEach(participant => {
        if (!participant) return;
        if (participant.type === 'department') {
            uniquePositiveIntegers(
                [participant.department_id].concat(
                    Array.isArray(participant.department_ids)
                        ? participant.department_ids
                        : []
                )
            ).forEach(id => {
                if (departmentIds.indexOf(id) === -1) departmentIds.push(id);
            });
        }
        if (participant.type === 'specific_users') {
            uniquePositiveIntegers(participant.user_ids).forEach(id => {
                if (userIds.indexOf(id) === -1) userIds.push(id);
            });
        }
    });
    departmentIds.sort((left, right) => left - right);
    userIds.sort((left, right) => left - right);
    const result = [];
    departmentIds.forEach(departmentId => {
        result.push({type: 'department', department_id: departmentId});
    });
    if (userIds.length) result.push({type: 'specific_users', user_ids: userIds});
    return result;
}

function mergeCardAccessRules(values) {
    const mergedByAudience = {};
    const result = [];
    const mergeRule = rule => {
        const key = JSON.stringify({scope: rule.scope, participants: rule.participants, ...(rule.scope === 'company' ? {targets: rule.targets} : {})});
        const existing = mergedByAudience[key];
        if (!existing) {
            mergedByAudience[key] = rule;
            result.push(rule);
            return;
        }
        rule.view_sections.forEach(section => {
            if (existing.view_sections.indexOf(section) === -1) {
                existing.view_sections.push(section);
            }
        });
        rule.manage_sections.forEach(section => {
            if (existing.manage_sections.indexOf(section) === -1) {
                existing.manage_sections.push(section);
            }
            if (existing.view_sections.indexOf(section) === -1) {
                existing.view_sections.push(section);
            }
        });
    };
    values.forEach((value, index) => {
        const rule = normalizeCardAccessRule(value, index);
        rule.participants = canonicalCardRuleParticipants(rule);
        if (rule.scope === 'object') {
            const roles = [];
            rule.participants.forEach(participant => {
                (participant.roles || []).forEach(role => {
                    if (roles.indexOf(role) === -1) roles.push(role);
                });
            });
            roles.forEach(role => {
                mergeRule(objectRoleCardAccessRule(
                    role,
                    rule.view_sections,
                    rule.manage_sections
                ));
            });
            return;
        }
        mergeRule(rule);
    });
    const objectRules = OBJECT_ROLE_OPTIONS.map(option => {
        const key = JSON.stringify({
            scope: 'object',
            participants: [{type: 'object_role', roles: [option.value]}]
        });
        return mergedByAudience[key] || objectRoleCardAccessRule(option.value);
    });
    return objectRules.concat(result.filter(rule => rule.scope === 'company'));
}

function normalizeSettings(value) {
    const source = value && typeof value === 'object' ? value : {};
    const defaults = defaultSettings();
    const access = source.access_config && typeof source.access_config === 'object'
        ? source.access_config
        : {};
    return {
        access_config: {
            view: normalizeScopedRight(access.view, defaults.access_config.view),
            move: normalizeScopedRight(access.move, defaults.access_config.move),
            multi_assignment_eligibility: normalizeParticipantConfig(
                access.multi_assignment_eligibility,
                defaults.access_config.multi_assignment_eligibility
            ),
            registry: normalizeParticipantConfig(
                access.registry,
                defaults.access_config.registry
            ),
            characteristics: normalizeParticipantConfig(
                access.characteristics,
                defaults.access_config.characteristics
            ),
            manage_employees: normalizeParticipantConfig(
                access.manage_employees,
                defaults.access_config.manage_employees
            ),
            import: normalizeParticipantConfig(
                access.import,
                defaults.access_config.import
            ),
            settings: normalizeParticipantConfig(
                access.settings,
                defaults.access_config.settings
            ),
            card_access_rules: mergeCardAccessRules(Array.isArray(access.card_access_rules)
                ? access.card_access_rules
                : defaults.access_config.card_access_rules
            )
        },
        default_department_ids: uniquePositiveIntegers(source.default_department_ids),
        show_assigned_outside_departments:
            source.show_assigned_outside_departments !== false,
        view_exclusions: normalizeViewExclusions(source.view_exclusions),
        planning_colors: Object.assign({}, source.planning_colors || {})
    };
}

function unwrapResponse(response) {
    return response && response.data && response.data.data
        ? response.data.data
        : (response ? response.data : null);
}

export default {
    name: 'EmployeeWorkforceSettings',
    components: {
        MultipleParticipantsConfig
    },
    data() {
        return {
            form: defaultSettings(),
            originalExclusions: null,
            loading: false,
            saving: false,
            hasLoaded: false,
            accessDenied: false,
            errorMessage: '',
            successMessage: '',
            exclusionCatalogEmployees: [],
            exclusionCatalogDepartments: [],
            expandedCardRuleIds: [],
            baseAccessTypes: BASE_ACCESS_TYPES,
            ownObjectAccessTypes: OWN_OBJECT_ACCESS_TYPES,
            multiAssignmentEligibilityTypes: MULTI_ASSIGNMENT_ELIGIBILITY_TYPES,
            objectRoleOptions: OBJECT_ROLE_OPTIONS,
            cardSections: CARD_SECTIONS,
            scopedRights: [
                {
                    key: 'view',
                    title: 'Просмотр доски',
                    description: 'Кто видит доску распределения, сотрудников на доступных объектах и их текущее место.'
                },
                {
                    key: 'move',
                    title: 'Перемещение сотрудников',
                    description: 'Кто может перемещать сотрудников между объектами, группами и общими зонами.'
                }
            ],
            simpleRights: [
                {
                    key: 'registry',
                    title: 'Полный реестр',
                    description: 'Кто видит полный список действующих, резервных и неподтверждённых сотрудников.',
                    hint: 'Это право не открывает доску распределения и не даёт доступ к учётным записям.',
                    emptyText: 'Полный реестр доступен только администраторам.'
                },
                {
                    key: 'manage_employees',
                    title: 'Основные рабочие данные',
                    description: 'Кто может менять рабочий статус, отдел, должность и приоритет сотрудника, сверять и объединять дубли карточек.',
                    hint: 'Объединение переносит связи в выбранную основную карточку и удаляет остальные после подтверждения. Конфликт двух учётных записей разрешает администратор. Разделы карточки настраиваются отдельно.',
                    emptyText: 'Основные рабочие данные меняют только администраторы.'
                },
                {
                    key: 'import',
                    title: 'Импорт и экспорт',
                    description: 'Кто может работать с выгрузками 1С и файлами Google Контактов.',
                    hint: 'Любой импорт начинается с предварительной проверки и сопоставления.',
                    emptyText: 'Импорт и экспорт доступны только администраторам.'
                },
                {
                    key: 'settings',
                    title: 'Управление настройками',
                    description: 'Кто может открывать и изменять этот экран.',
                    hint: 'Не удаляйте собственный доступ, если он ещё понадобится.',
                    emptyText: 'Настройки доступны только администраторам.'
                }
            ]
        };
    },
    computed: {
        registryRight() {
            return this.simpleRights.find(right => right.key === 'registry') || null;
        },
        actionRights() {
            return this.simpleRights.filter(right => right.key !== 'registry');
        },
        participantUsers() {
            return (this.$store.state.users || [])
                .filter(user => !user.isBot)
                .map(user => ({
                    value: Number(user.user_id || user.id),
                    text: this.userName(user)
                }))
                .filter(option => option.value)
                .sort((left, right) => left.text.localeCompare(right.text, 'ru'));
        },
        participantDepartments() {
            return (this.$store.state.departments || [])
                .filter(department => department.is_active !== false)
                .map(department => ({
                    value: Number(department.id || department.department_id),
                    text: department.name || `Отдел #${department.id || department.department_id}`
                }))
                .filter(option => option.value)
                .sort((left, right) => left.text.localeCompare(right.text, 'ru'));
        },
        cardTargetEmployeeOptions() {
            return this.exclusionCatalogEmployees.map(employee => ({
                value: Number(employee.employee_id),
                text: employee.display_name || `Сотрудник #${employee.employee_id}`
            }));
        },
        defaultDepartmentOptions() {
            const options = this.participantDepartments.slice();
            const known = {};
            options.forEach(option => {
                known[option.value] = true;
            });
            (this.form.default_department_ids || []).forEach(departmentId => {
                if (!known[departmentId]) {
                    options.push({
                        value: departmentId,
                        text: `Отдел #${departmentId}`
                    });
                }
            });
            return options;
        },
        exclusionDepartmentOptions() {
            const selected = uniquePositiveIntegers(
                this.form.view_exclusions.board.department_ids.concat(
                    this.form.view_exclusions.plan.department_ids
                )
            );
            const source = this.exclusionCatalogDepartments.length
                ? this.exclusionCatalogDepartments
                : (this.$store.state.departments || []);
            const options = source.filter(department => (
                department.is_active !== false
                || selected.indexOf(Number(department.id || department.department_id)) !== -1
            )).map(department => ({
                value: Number(department.id || department.department_id),
                text: department.name
                    || `Отдел #${department.id || department.department_id}`
            })).filter(option => option.value);
            const known = {};
            options.forEach(option => { known[option.value] = true; });
            selected.forEach(departmentId => {
                if (!known[departmentId]) {
                    options.push({value: departmentId, text: `Отдел #${departmentId}`});
                }
            });
            return options.sort((left, right) => left.text.localeCompare(right.text, 'ru'));
        },
        exclusionEmployeeOptions() {
            const selected = uniquePositiveIntegers(
                this.form.view_exclusions.board.employee_ids.concat(
                    this.form.view_exclusions.plan.employee_ids,
                    this.form.view_exclusions.board.included_employee_ids,
                    this.form.view_exclusions.plan.included_employee_ids
                )
            );
            const statuses = {
                reserve: 'резерв',
                unverified: 'не подтверждён'
            };
            const options = this.exclusionCatalogEmployees.map(employee => {
                const department = this.exclusionDepartmentOptions.find(option => (
                    option.value === Number(employee.operational_department_id)
                ));
                const details = [];
                if (department) details.push(department.text);
                if (statuses[employee.lifecycle_status]) {
                    details.push(statuses[employee.lifecycle_status]);
                }
                return {
                    value: Number(employee.employee_id),
                    text: `${employee.display_name || `Сотрудник #${employee.employee_id}`}`
                        + (details.length ? ` · ${details.join(', ')}` : '')
                };
            }).filter(option => option.value);
            const known = {};
            options.forEach(option => { known[option.value] = true; });
            selected.forEach(employeeId => {
                if (!known[employeeId]) {
                    options.push({
                        value: employeeId,
                        text: `Сотрудник #${employeeId}`
                    });
                }
            });
            return options.sort((left, right) => left.text.localeCompare(right.text, 'ru'));
        }
    },
    mounted() {
        this.loadSettings();
    },
    methods: {
        syncVisibilitySelection(view, field) {
            const other = field === 'employee_ids' ? 'included_employee_ids' : 'employee_ids';
            const selected = this.form.view_exclusions[view][field];
            this.form.view_exclusions[view][other] = this.form.view_exclusions[view][other].filter(id => !selected.includes(id));
        },
        async loadSettings() {
            this.loading = true;
            this.errorMessage = '';
            this.successMessage = '';
            try {
                const response = await api.get('/api/employees/workforce/settings', {
                    ignoreModal: true
                });
                this.form = normalizeSettings(unwrapResponse(response));
                this.originalExclusions = cloneJson(this.form.view_exclusions);
                this.hasLoaded = true;
                this.accessDenied = false;
                await Promise.all([
                    this.$store.dispatch('getUsers', {full: true}).catch(() => null),
                    this.$store.dispatch('getDepartments').catch(() => null),
                    this.loadExclusionCatalog()
                ]);
            } catch (error) {
                if (error && error.response && error.response.status === 403) {
                    this.accessDenied = true;
                    this.hasLoaded = false;
                } else {
                    this.errorMessage = this.errorText(
                        error,
                        'Не удалось загрузить настройки модуля «Сотрудники»'
                    );
                }
            } finally {
                this.loading = false;
            }
        },
        async loadExclusionCatalog() {
            const response = await api.get('/api/employees/workforce/settings/catalog', {
                ignoreModal: true
            });
            const payload = unwrapResponse(response) || {};
            this.exclusionCatalogEmployees = Array.isArray(payload.employees)
                ? payload.employees
                : [];
            this.exclusionCatalogDepartments = Array.isArray(payload.departments)
                ? payload.departments
                : [];
        },
        async saveSettings() {
            if (this.saving || this.loading) {
                return;
            }
            const incompleteRuleIndex = this.form.access_config.card_access_rules.findIndex(
                rule => !this.cardRuleHasAudience(rule)
                    || (
                        rule.scope === 'company'
                        && (
                            !Array.isArray(rule.view_sections)
                            || !rule.view_sections.length
                        )
                    )
            );
            if (incompleteRuleIndex !== -1) {
                const rule = this.form.access_config.card_access_rules[incompleteRuleIndex];
                if (!this.isCardRuleExpanded(rule)) {
                    this.expandedCardRuleIds.push(rule.id);
                }
                this.errorMessage = !this.cardRuleHasAudience(rule)
                    ? `В правиле ${incompleteRuleIndex + 1} не выбраны получатели.`
                    : `В правиле ${incompleteRuleIndex + 1} не выбран ни один раздел карточки.`;
                return;
            }
            this.saving = true;
            this.errorMessage = '';
            this.successMessage = '';
            try {
                const response = await api.put(
                    '/api/employees/workforce/settings',
                    Object.assign(normalizeSettings(this.form), {expected_view_exclusions: this.originalExclusions}),
                    {ignoreModal: true}
                );
                this.form = normalizeSettings(unwrapResponse(response));
                this.originalExclusions = cloneJson(this.form.view_exclusions);
                this.successMessage = 'Настройки модуля «Сотрудники» сохранены.';
            } catch (error) {
                if (error && error.response && error.response.status === 403) {
                    this.accessDenied = true;
                    this.hasLoaded = false;
                } else {
                    this.errorMessage = this.errorText(
                        error,
                        'Не удалось сохранить настройки модуля «Сотрудники»'
                    );
                }
            } finally {
                this.saving = false;
            }
        },
        onScopedAccessInput(right, scope, value) {
            this.$set(
                this.form.access_config[right],
                scope,
                normalizeParticipantConfig(value, emptyParticipantConfig())
            );
        },
        onSimpleAccessInput(right, value) {
            this.$set(
                this.form.access_config,
                right,
                normalizeParticipantConfig(value, emptyParticipantConfig())
            );
        },
        onMultiAssignmentInput(value) {
            this.$set(
                this.form.access_config,
                'multi_assignment_eligibility',
                normalizeParticipantConfig(value, emptyParticipantConfig())
            );
        },
        cardRuleHasSection(rule, field, section) {
            return Array.isArray(rule[field]) && rule[field].indexOf(section) !== -1;
        },
        cardRuleParticipantRows(rule, type) {
            return (Array.isArray(rule && rule.participants) ? rule.participants : [])
                .filter(participant => participant && participant.type === type);
        },
        cardRuleObjectRoles(rule) {
            const roles = [];
            this.cardRuleParticipantRows(rule, 'object_role').forEach(participant => {
                (Array.isArray(participant.roles) ? participant.roles : []).forEach(role => {
                    if (roles.indexOf(role) === -1) roles.push(role);
                });
            });
            return roles;
        },
        cardRuleHasObjectRole(rule, role) {
            return this.cardRuleObjectRoles(rule).indexOf(role) !== -1;
        },
        cardRuleCompanyMode(rule) {
            return this.cardRuleParticipantRows(rule, 'all').length ? 'all' : 'selected';
        },
        setCardRuleTargetMode(rule, mode) {
            this.$set(rule, 'targets', normalizeCardTargets({mode}));
        },
        cardRuleTargetSummary(rule) {
            if (rule.targets.mode === 'all') return 'Все сотрудники';
            const labels = rule.targets.department_ids.map(id => {
                const option = this.participantDepartments.find(item => item.value === id);
                return option ? option.text : `Отдел #${id}`;
            }).concat(rule.targets.employee_ids.map(id => {
                const option = this.cardTargetEmployeeOptions.find(item => item.value === id);
                return option ? option.text : `Сотрудник #${id}`;
            }));
            return this.compactCardRuleLabels(labels, 'Никто не выбран');
        },
        setCardRuleCompanyMode(index, mode) {
            const rule = this.form.access_config.card_access_rules[index];
            if (!rule) return;
            if (mode === 'all') {
                this.$set(rule, 'participants', [{type: 'all'}]);
                return;
            }
            this.$set(
                rule,
                'participants',
                (rule.participants || []).filter(participant => participant.type !== 'all')
            );
        },
        cardRuleCompanyDepartmentIds(rule) {
            const ids = [];
            this.cardRuleParticipantRows(rule, 'department').forEach(participant => {
                uniquePositiveIntegers(
                    [participant.department_id].concat(
                        Array.isArray(participant.department_ids)
                            ? participant.department_ids
                            : []
                    )
                ).forEach(id => {
                    if (ids.indexOf(id) === -1) ids.push(id);
                });
            });
            return ids;
        },
        cardRuleCompanyUserIds(rule) {
            const ids = [];
            this.cardRuleParticipantRows(rule, 'specific_users').forEach(participant => {
                uniquePositiveIntegers(participant.user_ids).forEach(id => {
                    if (ids.indexOf(id) === -1) ids.push(id);
                });
            });
            return ids;
        },
        setCardRuleCompanyDepartments(index, values) {
            const rule = this.form.access_config.card_access_rules[index];
            if (!rule) return;
            const participants = (rule.participants || []).filter(participant => (
                participant.type !== 'department' && participant.type !== 'all'
            ));
            uniquePositiveIntegers(values).forEach(departmentId => {
                participants.push({type: 'department', department_id: departmentId});
            });
            this.$set(rule, 'participants', participants);
        },
        setCardRuleCompanyUsers(index, values) {
            this.setCardRuleCompanyParticipant(
                index,
                'specific_users',
                'user_ids',
                values
            );
        },
        setCardRuleCompanyParticipant(index, type, field, values) {
            const rule = this.form.access_config.card_access_rules[index];
            if (!rule) return;
            const participants = (rule.participants || []).filter(participant => (
                participant.type !== type && participant.type !== 'all'
            ));
            const ids = uniquePositiveIntegers(values);
            if (ids.length) participants.push({type, [field]: ids});
            this.$set(rule, 'participants', participants);
        },
        cardRuleHasAudience(rule) {
            if (rule.scope === 'object') return Boolean(this.cardRuleObjectRoles(rule).length);
            return this.cardRuleCompanyMode(rule) === 'all'
                || Boolean(
                    this.cardRuleCompanyDepartmentIds(rule).length
                    || this.cardRuleCompanyUserIds(rule).length
                );
        },
        cardRuleAudienceSummary(rule) {
            if (rule.scope === 'object') {
                const roleLabels = this.cardRuleObjectRoles(rule).map(role => {
                    const option = this.objectRoleOptions.find(item => item.value === role);
                    return option ? option.text : role;
                });
                return this.compactCardRuleLabels(roleLabels, 'Роли не выбраны');
            }
            if (this.cardRuleCompanyMode(rule) === 'all') return 'Все пользователи';
            const labels = [];
            this.cardRuleCompanyDepartmentIds(rule).forEach(id => {
                const option = this.participantDepartments.find(item => item.value === id);
                labels.push(option ? option.text : `Отдел #${id}`);
            });
            this.cardRuleCompanyUserIds(rule).forEach(id => {
                const option = this.participantUsers.find(item => item.value === id);
                labels.push(option ? option.text : `Сотрудник #${id}`);
            });
            return this.compactCardRuleLabels(labels, 'Получатели не выбраны');
        },
        compactCardRuleLabels(labels, emptyText) {
            if (!labels.length) return emptyText;
            if (labels.length <= 2) return labels.join(', ');
            return `${labels.slice(0, 2).join(', ')} и ещё ${labels.length - 2}`;
        },
        cardRulePermissionSummary(rule) {
            const viewCount = Array.isArray(rule.view_sections)
                ? rule.view_sections.length
                : 0;
            const manageCount = Array.isArray(rule.manage_sections)
                ? rule.manage_sections.length
                : 0;
            if (!viewCount) return 'Доступ к разделам не выбран';
            return `${viewCount} для просмотра · ${manageCount} для изменения`;
        },
        isCardRuleExpanded(rule) {
            return this.expandedCardRuleIds.indexOf(rule.id) !== -1;
        },
        toggleCardRule(rule) {
            const index = this.expandedCardRuleIds.indexOf(rule.id);
            if (index === -1) this.expandedCardRuleIds.push(rule.id);
            else this.expandedCardRuleIds.splice(index, 1);
        },
        addCardAccessRule() {
            const index = this.form.access_config.card_access_rules.length;
            const rule = normalizeCardAccessRule({
                id: `card_rule_${Date.now()}_${index + 1}`,
                scope: 'company',
                participants: [],
                view_sections: ['contacts'],
                manage_sections: []
            }, index);
            this.form.access_config.card_access_rules.push(rule);
            this.expandedCardRuleIds.push(rule.id);
        },
        removeCardAccessRule(index) {
            const rule = this.form.access_config.card_access_rules[index];
            if (!rule || rule.scope === 'object') return;
            const removed = this.form.access_config.card_access_rules.splice(index, 1)[0];
            if (!removed) return;
            const expandedIndex = this.expandedCardRuleIds.indexOf(removed.id);
            if (expandedIndex !== -1) this.expandedCardRuleIds.splice(expandedIndex, 1);
        },
        setCardRuleSection(index, field, section, checked) {
            const rule = this.form.access_config.card_access_rules[index];
            if (!rule) return;
            const values = Array.isArray(rule[field]) ? rule[field].slice() : [];
            const position = values.indexOf(section);
            if (checked && position === -1) values.push(section);
            if (!checked && position !== -1) values.splice(position, 1);
            this.$set(rule, field, values);
            if (field === 'manage_sections' && checked) {
                this.setCardRuleSection(index, 'view_sections', section, true);
            }
            if (field === 'view_sections' && !checked) {
                this.setCardRuleSection(index, 'manage_sections', section, false);
            }
        },
        cardRuleSectionLevel(rule, section) {
            if (this.cardRuleHasSection(rule, 'manage_sections', section)) return 'manage';
            if (this.cardRuleHasSection(rule, 'view_sections', section)) return 'view';
            return 'none';
        },
        setCardRuleSectionLevel(index, section, level) {
            this.setCardRuleSection(index, 'manage_sections', section, level === 'manage');
            this.setCardRuleSection(
                index,
                'view_sections',
                section,
                level === 'view' || level === 'manage'
            );
        },
        applyCardRulePreset(index, preset) {
            const rule = this.form.access_config.card_access_rules[index];
            if (!rule) return;
            if (preset === 'none') {
                this.$set(rule, 'view_sections', []);
                this.$set(rule, 'manage_sections', []);
                return;
            }
            this.$set(rule, 'view_sections', CARD_VIEW_SECTION_KEYS.slice());
            this.$set(
                rule,
                'manage_sections',
                preset === 'manage' ? CARD_MANAGE_SECTION_KEYS.slice() : []
            );
        },
        userName(user) {
            if (!user) {
                return '';
            }
            return user.full_name ||
                [user.last_name, user.first_name].filter(Boolean).join(' ').trim() ||
                `Сотрудник #${user.user_id || user.id}`;
        },
        errorText(error, fallback) {
            const data = error && error.response ? error.response.data : null;
            if (data && typeof data === 'object') {
                return data.msg || data.message || fallback;
            }
            if (typeof data === 'string' && data) {
                return data;
            }
            return fallback;
        },
        goToBoard() {
            this.$router.push({name: 'employees-workforce'}).catch(error => {
                if (!error || error.name !== 'NavigationDuplicated') {
                    throw error;
                }
            });
        }
    }
};
</script>

<style scoped>
.workforce-settings-page {
    max-width: 1440px;
    margin: 0 auto;
    padding: 1.25rem;
    color: #263442;
}

.workforce-settings-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.25rem;
}

.workforce-settings-header__title {
    display: flex;
    align-items: flex-start;
    gap: 0.7rem;
}

.workforce-settings-header__title h1 {
    margin: 0;
    font-size: 1.75rem;
    font-weight: 700;
}

.workforce-settings-header__title p {
    margin: 0.25rem 0 0;
    color: #687888;
}

.workforce-settings-header__back {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-top: 0.15rem;
    padding-left: 0;
    white-space: nowrap;
}

.workforce-settings-header__actions {
    display: flex;
    flex-shrink: 0;
    gap: 0.5rem;
}

.workforce-settings-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.65rem;
    min-height: 220px;
    color: #687888;
}

.workforce-settings-main-tabs {
    margin-bottom: 0.75rem;
    border: 0;
    background: transparent;
}

.workforce-settings-main-tabs >>> .workforce-settings-main-tabs__nav {
    gap: 0.35rem;
    padding: 0.35rem;
    border: 1px solid #d8e2ec;
    border-radius: 0.7rem;
    background: #eef3f8;
}

.workforce-settings-main-tabs >>> .workforce-settings-main-tabs__nav .nav-link {
    color: #506579;
    font-weight: 700;
}

.workforce-settings-main-tabs >>> .workforce-settings-main-tabs__nav .nav-link.active {
    background: #176ff2;
    color: #fff;
    box-shadow: 0 2px 5px rgba(23, 111, 242, 0.2);
}

.workforce-settings-section {
    margin-bottom: 1.25rem;
    padding: 1.25rem;
    border: 1px solid #dfe6ed;
    border-radius: 0.75rem;
    background: #fff;
    box-shadow: 0 2px 8px rgba(30, 55, 80, 0.06);
}

.workforce-settings-section__heading {
    display: flex;
    align-items: flex-start;
    gap: 0.8rem;
    margin-bottom: 1rem;
}

.workforce-settings-section__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 2.4rem;
    width: 2.4rem;
    height: 2.4rem;
    border-radius: 50%;
    background: #e8f2ff;
    color: #1769c2;
}

.workforce-settings-section__heading h2 {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 700;
}

.workforce-settings-section__heading p {
    margin: 0.25rem 0 0;
    color: #687888;
}

.workforce-access-intro {
    margin-bottom: 1rem;
}

.workforce-access-intro h3 {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
}

.workforce-access-intro p {
    margin: 0.25rem 0 0;
    color: #687888;
}

.workforce-access-scopes {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
}

.workforce-access-scope {
    min-width: 0;
    padding: 1rem;
    border: 1px solid #dfe6ed;
    border-radius: 0.55rem;
    background: #f9fbfd;
}

.workforce-access-scope--single {
    max-width: 760px;
}

.workforce-access-scope h4 {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
}

.workforce-access-scope > p {
    min-height: 2.6rem;
    margin: 0.3rem 0 1rem;
    color: #687888;
    font-size: 0.88rem;
}

.workforce-list-composition-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
}

.workforce-list-composition-card {
    min-width: 0;
    padding: 1rem;
    border: 1px solid #d7e1eb;
    border-radius: 0.65rem;
    background: #f8fbfe;
}

.workforce-list-composition-card h3 {
    margin: 0;
    color: #2d4358;
    font-size: 1rem;
    font-weight: 750;
}

.workforce-list-composition-card > p {
    min-height: 2.6rem;
    margin: 0.3rem 0 1rem;
    color: #687888;
    font-size: 0.88rem;
}

.workforce-card-access-heading {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(18rem, 26rem);
    align-items: start;
    gap: 1rem;
    margin-bottom: 1rem;
}

.workforce-card-access-heading h3 {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 750;
}

.workforce-card-access-heading p {
    margin: 0.3rem 0 0;
    color: #687888;
}

.workforce-card-access-heading__note {
    display: flex;
    align-items: flex-start;
    gap: 0.65rem;
    padding: 0.75rem 0.85rem;
    border: 1px solid #cfe0f4;
    border-radius: 0.65rem;
    background: #f2f7fd;
    color: #425a70;
    font-size: 0.84rem;
    line-height: 1.35;
}

.workforce-card-access-heading__note i {
    margin-top: 0.15rem;
    color: #1769c2;
}

.workforce-card-access-create {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.9rem 1rem;
    border: 1px solid #cbd9e8;
    border-radius: 0.7rem;
    background: #f8fbff;
}

.workforce-card-access-create strong,
.workforce-card-access-create span {
    display: block;
}

.workforce-card-access-create span {
    margin-top: 0.15rem;
    color: #6a7b8c;
    font-size: 0.84rem;
}

.workforce-card-access-create__actions {
    display: flex;
    flex: 0 0 auto;
    gap: 0.5rem;
}

.workforce-card-access-empty {
    margin-top: 0.75rem;
    padding: 1rem;
    border: 1px dashed #b8c7d5;
    border-radius: 0.65rem;
    color: #687888;
    text-align: center;
}

.workforce-card-access-rule {
    margin-top: 0.65rem;
    overflow: hidden;
    border: 1px solid #d6e0ea;
    border-radius: 0.72rem;
    background: #fff;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.workforce-card-access-rule--open {
    border-color: #9fc5ef;
    box-shadow: 0 5px 18px rgba(39, 92, 145, 0.1);
}

.workforce-card-access-rule__header {
    display: flex;
    align-items: stretch;
}

.workforce-card-access-rule__toggle {
    display: grid;
    grid-template-columns: 8.3rem minmax(0, 1fr) 1.25rem;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
    gap: 0.85rem;
    padding: 0.82rem 0.9rem;
    border: 0;
    background: transparent;
    color: inherit;
    text-align: left;
}

.workforce-card-access-rule__toggle:hover {
    background: #f8fbfe;
}

.workforce-card-access-rule__toggle:focus {
    outline: 3px solid rgba(23, 105, 194, 0.18);
    outline-offset: -3px;
}

.workforce-card-access-rule__scope {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 1.75rem;
    padding: 0.25rem 0.55rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.025em;
    text-transform: uppercase;
}

.workforce-card-access-rule__scope--object {
    background: #e8f3ff;
    color: #135fae;
}

.workforce-card-access-rule__scope--company {
    background: #eee9ff;
    color: #6245a5;
}

.workforce-card-access-rule__summary {
    display: block;
    min-width: 0;
}

.workforce-card-access-rule__summary strong,
.workforce-card-access-rule__summary small {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workforce-card-access-rule__summary strong {
    color: #243647;
    font-size: 0.96rem;
}

.workforce-card-access-rule__summary small {
    margin-top: 0.18rem;
    color: #708193;
    font-size: 0.8rem;
}

.workforce-card-access-rule__chevron {
    color: #718394;
    transition: transform 0.15s ease;
}

.workforce-card-access-rule__chevron--open {
    transform: rotate(180deg);
}

.workforce-card-access-rule__delete {
    flex: 0 0 2.75rem;
    border-left: 1px solid #e5ebf1;
    border-radius: 0;
    color: #a44d55;
}

.workforce-card-access-rule__delete:hover {
    background: #fff3f4;
    color: #b52835;
}

.workforce-card-access-rule__body {
    padding: 1rem;
    border-top: 1px solid #dfe7ef;
    background: #fbfcfe;
}

.workforce-card-access-audience,
.workforce-card-access-permissions {
    padding: 1rem;
    border: 1px solid #e0e7ee;
    border-radius: 0.65rem;
    background: #fff;
}

.workforce-card-access-permissions {
    margin-top: 0.75rem;
}

.workforce-card-access-audience + .workforce-card-access-audience {
    margin-top: 0.75rem;
}

.workforce-card-access-step {
    display: flex;
    align-items: flex-start;
    gap: 0.65rem;
}

.workforce-card-access-step > span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 1.65rem;
    width: 1.65rem;
    height: 1.65rem;
    border-radius: 50%;
    background: #1769c2;
    color: #fff;
    font-size: 0.8rem;
    font-weight: 800;
}

.workforce-card-access-step strong,
.workforce-card-access-step small {
    display: block;
}

.workforce-card-access-step strong {
    color: #25384a;
}

.workforce-card-access-step small {
    margin-top: 0.15rem;
    color: #708193;
    font-size: 0.8rem;
}

.workforce-card-access-role-fixed {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    margin-top: 0.9rem;
    padding: 0.7rem 0.8rem;
    border: 1px solid #bdd6ef;
    border-radius: 0.55rem;
    background: #f1f7fd;
    color: #2d587e;
}

.workforce-card-access-role-fixed > i {
    color: #1769c2;
    font-size: 1.15rem;
}

.workforce-card-access-role-fixed strong,
.workforce-card-access-role-fixed small {
    display: block;
}

.workforce-card-access-role-fixed small {
    margin-top: 0.12rem;
    color: #668097;
    font-size: 0.78rem;
}

.workforce-card-access-company-mode {
    display: inline-flex;
    margin-top: 0.9rem;
    padding: 0.2rem;
    border: 1px solid #ccd8e4;
    border-radius: 0.55rem;
    background: #f0f4f8;
}

.workforce-card-access-company-mode button {
    padding: 0.48rem 0.75rem;
    border: 0;
    border-radius: 0.4rem;
    background: transparent;
    color: #5f7285;
    font-weight: 650;
}

.workforce-card-access-company-mode button.is-active {
    background: #fff;
    color: #1769c2;
    box-shadow: 0 1px 4px rgba(34, 62, 90, 0.14);
}

.workforce-card-access-company-fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
    margin-top: 0.8rem;
}

.workforce-card-access-warning {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    margin-top: 0.75rem;
    color: #985b00;
    font-size: 0.84rem;
    font-weight: 650;
}

.workforce-card-access-permissions__heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
}

.workforce-card-access-presets {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.35rem;
}

.workforce-card-access-presets button {
    padding: 0.32rem 0.55rem;
    border: 0;
    border-radius: 0.4rem;
    background: #edf3f8;
    color: #536a7f;
    font-size: 0.78rem;
    font-weight: 650;
}

.workforce-card-access-presets button:hover {
    background: #dfeaf4;
    color: #245d92;
}

.workforce-card-access-sections {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.55rem;
    margin-top: 0.9rem;
}

.workforce-card-access-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-width: 0;
    gap: 0.75rem;
    padding: 0.65rem;
    border: 1px solid #e1e7ed;
    border-radius: 0.55rem;
    background: #fcfdff;
}

.workforce-card-access-section__label {
    min-width: 0;
}

.workforce-card-access-section__label strong,
.workforce-card-access-section__label small {
    display: block;
}

.workforce-card-access-section__label strong {
    color: #314456;
    font-size: 0.86rem;
}

.workforce-card-access-section__label small {
    max-width: 16rem;
    margin-top: 0.12rem;
    color: #788898;
    font-size: 0.72rem;
    line-height: 1.25;
}

.workforce-card-access-levels {
    display: inline-flex;
    flex: 0 0 auto;
    overflow: hidden;
    border: 1px solid #cdd8e2;
    border-radius: 0.45rem;
}

.workforce-card-access-levels button {
    min-height: 2rem;
    padding: 0.34rem 0.5rem;
    border: 0;
    border-left: 1px solid #d9e1e8;
    background: #fff;
    color: #687b8d;
    font-size: 0.75rem;
    font-weight: 650;
}

.workforce-card-access-levels button:first-child {
    border-left: 0;
}

.workforce-card-access-levels button.is-active {
    color: #fff;
}

.workforce-card-access-levels button.is-none {
    background: #6f7f8e;
}

.workforce-card-access-levels button.is-view {
    background: #1769c2;
}

.workforce-card-access-levels button.is-manage {
    background: #18815d;
}

.workforce-settings-footer {
    position: sticky;
    bottom: 0;
    z-index: 10;
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 0.85rem 0;
    background: rgba(247, 249, 251, 0.96);
}

@media (max-width: 991px) {
    .workforce-access-scopes,
    .workforce-list-composition-grid {
        grid-template-columns: 1fr;
    }

    .workforce-access-scope > p,
    .workforce-list-composition-card > p {
        min-height: 0;
    }

    .workforce-card-access-heading,
    .workforce-card-access-company-fields,
    .workforce-card-access-sections {
        grid-template-columns: 1fr;
    }

    .workforce-card-access-create,
    .workforce-card-access-permissions__heading {
        align-items: stretch;
        flex-direction: column;
    }

    .workforce-card-access-presets {
        justify-content: flex-start;
    }
}

@media (max-width: 767px) {
    .workforce-settings-page {
        padding: 0.8rem;
    }

    .workforce-settings-header {
        flex-direction: column;
    }

    .workforce-settings-header__title {
        display: block;
    }

    .workforce-settings-header__back {
        margin-bottom: 0.5rem;
    }

    .workforce-settings-header__title h1 {
        font-size: 1.35rem;
    }

    .workforce-settings-header__actions {
        width: 100%;
    }

    .workforce-settings-header__actions .btn {
        flex: 1 1 0;
    }

    .workforce-settings-section {
        padding: 0.9rem;
    }

    .workforce-settings-section__icon {
        display: none;
    }

    .workforce-settings-footer {
        padding-bottom: calc(0.85rem + env(safe-area-inset-bottom));
    }

    .workforce-settings-footer .btn {
        flex: 1 1 0;
    }

    .workforce-card-access-create__actions {
        display: grid;
        grid-template-columns: 1fr;
        width: 100%;
    }

    .workforce-card-access-rule__toggle {
        grid-template-columns: minmax(0, 1fr) 1rem;
        gap: 0.35rem 0.6rem;
        padding: 0.75rem;
    }

    .workforce-card-access-rule__scope {
        grid-column: 1;
        justify-self: start;
    }

    .workforce-card-access-rule__summary {
        grid-column: 1;
    }

    .workforce-card-access-rule__chevron {
        grid-row: 1 / span 2;
        grid-column: 2;
    }

    .workforce-card-access-rule__delete {
        flex-basis: 2.5rem;
    }

    .workforce-card-access-rule__body,
    .workforce-card-access-audience,
    .workforce-card-access-permissions {
        padding: 0.7rem;
    }

    .workforce-card-access-company-mode,
    .workforce-card-access-presets {
        display: grid;
        grid-template-columns: 1fr;
        width: 100%;
    }

    .workforce-card-access-company-mode {
        gap: 0.2rem;
    }

    .workforce-card-access-section {
        align-items: stretch;
        flex-direction: column;
    }

    .workforce-card-access-levels {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        width: 100%;
    }

    .workforce-card-access-levels--view-only {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .workforce-card-access-levels button {
        padding-right: 0.3rem;
        padding-left: 0.3rem;
    }

}
</style>
